import { createTheme } from '@mui/material/styles';
import { getTenantColors } from './utils/colors';
import { typography as typographyUtils } from './utils/typography';
import { spacing as spacingUtils } from './utils/spacing';

/**
 * MUI 테마 생성 함수
 * @param {object} colors - 색상 객체 (테넌트별 색상)
 * @returns {object} MUI 테마 객체
 */
export const createAppTheme = (colors = getTenantColors()) => {
  return createTheme({
  cssVariables: {
    nativeColor: true,
  },
  palette: {
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary,
      contrastText: '#ffffff',
    },
    background: {
      default: colors.backgroundDefault,
      paper: '#ffffff',
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    divider: colors.borderLight,
    error: {
      main: colors.error,
    },
    warning: {
      main: colors.warning,
    },
    info: {
      main: colors.primary,
    },
    success: {
      main: colors.success,
    },
    logout: {
      main: colors.logoutButton,
      hover: colors.logoutButtonHover,
      active: colors.logoutButtonActive,
      border: colors.border,
      borderHover: colors.borderDark,
      text: colors.textPrimary,
      textHover: colors.textSecondary,
    },
  },
  typography: {
    fontFamily: typographyUtils.fontFamily,
    fontSize: parseInt(typographyUtils.fontSize.sm),
    fontSizeScale: typographyUtils.fontSize,
    fontWeight: typographyUtils.fontWeight,
    lineHeight: typographyUtils.lineHeight,
    h1: {
      fontSize: typographyUtils.fontSize.xl,
      fontWeight: typographyUtils.fontWeight.bold,
    },
    body1: {
      fontSize: typographyUtils.fontSize.sm,
    },
    body2: {
      fontSize: typographyUtils.fontSize.xs,
    },
  },
  spacing: (factor) => `${factor * 2}px`,
  // Custom spacing values (spacingUtils를 theme에 통합)
  custom: {
    spacing: spacingUtils,
  },
  shape: {
    borderRadius: 2,
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            height: theme.custom.spacing.buttonHeight,
            fontSize: theme.typography.fontSizeScale.sm,
            backgroundColor: theme.palette.background.paper + ' !important',
            borderRadius: theme.shape.borderRadius + 'px',
            fontFamily: theme.typography.fontFamily,
            '& fieldset': {
              borderColor: theme.palette.divider,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
            },
          },
          '& .MuiOutlinedInput-input': {
            fontSize: theme.typography.fontSizeScale.sm,
            padding: '1.5px 6px 2.5px 6px !important',
            textAlign: 'left',
          },
          '& .MuiInputAdornment-root': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiIconButton-root': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& label.Mui-focused': {
            color: theme.palette.primary.light,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginLeft: theme.custom.spacing.buttonSpacing,
          fontSize: theme.typography.fontSizeScale.sm,
          fontWeight: theme.typography.fontWeight.normal,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: theme.custom.spacing.buttonHeight,
          fontSize: theme.typography.fontSizeScale.sm,
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.divider,
          '&.Mui-checked': {
            color: colors.checkbox,
          },
        }),
      },
    },
  },
  });
};

/**
 * 기본 테마 (하위 호환성을 위해 유지)
 * 테넌트별 색상을 사용하는 경우 createAppTheme()를 사용하세요
 */
export const theme = createAppTheme();

export default theme;

